import { Severity, useAlarmsFindingsQuery } from '@/__generated__/types';

import { UNRESOLVED_SCOPE } from '../constants';
import type {
  AlarmSeverityCounts,
  AlarmSeverityLevel,
  SpaceScope,
} from '../types/home-types';

export interface UseAlarmsFindingsParams {
  scope?: SpaceScope;
}

// ponytail: the severity split is capped; the headline count comes from alarmsCount so it stays exact.
const ALARMS_LIMIT = 1000;

const SEVERITY_LEVEL: Record<Severity, AlarmSeverityLevel> = {
  [Severity.High]: 'high',
  [Severity.Medium]: 'medium',
  [Severity.Low]: 'low',
};

export const countBySeverity = (
  alarms: { severity: Severity }[],
): AlarmSeverityCounts => {
  const counts: AlarmSeverityCounts = { high: 0, medium: 0, low: 0 };
  for (const alarm of alarms) {
    counts[SEVERITY_LEVEL[alarm.severity]] += 1;
  }
  return counts;
};

/** Real-time alarm and finding counts — deliberately not filtered by the period. */
export const useAlarmsFindings = ({ scope }: UseAlarmsFindingsParams) => {
  const { data, loading, error, refetch } = useAlarmsFindingsQuery({
    skip: !scope,
    variables: { ...(scope ?? UNRESOLVED_SCOPE), limit: ALARMS_LIMIT },
    errorPolicy: 'all',
  });

  const active = data?.alarmsCount?.active;
  const activeCount = active?.count ?? 0;
  const findingsCount = active?.findings ?? 0;

  return {
    activeCount,
    findingsCount,
    notDiagnosed: Math.max(activeCount - findingsCount, 0),
    bySeverity: countBySeverity(data?.alarms ?? []),
    isLoading: loading || !scope,
    isError: !!error,
    isEmpty: !loading && active == null,
    retry: () => {
      void refetch();
    },
  };
};
